import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Card, KPI, Empty, Button, Input, Select, Label, Textarea } from "@/components/Primitives";
import { StatusPill, invoiceStatusVariant, ticketStatusVariant } from "@/components/StatusPill";
import { ArrowLeft, Mail, Phone, Briefcase, Pencil, X, KeyRound, Copy, Eye, EyeOff, RefreshCw, ShieldOff, Check, Boxes, Plus, Trash2, ExternalLink, Repeat, Receipt, Zap } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/context/TenantContext";

function gbp(v) {
  const n = v ?? 0;
  const hasPence = Math.round(n * 100) !== Math.round(n) * 100;
  return `£${n.toLocaleString(undefined, { minimumFractionDigits: hasPence ? 2 : 0, maximumFractionDigits: 2 })}`;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [grantingAccess, setGrantingAccess] = useState(false);
  const [access, setAccess] = useState(null);
  const [quickCharge, setQuickCharge] = useState(false);

  const load = async () => {
    try {
      const c = await api.get(`/customers/${id}`);
      const [apps, invoices, projects, tickets, ax] = await Promise.all([
        api.get("/apps").then((r) => (r.data || []).filter((a) => a.customer_id === id || (a.customer_ids || []).includes(id))),
        api.get("/invoices").then((r) => (r.data || []).filter((i) => i.customer_id === id && i.status !== "draft")),
        api.get("/projects").then((r) => (r.data || []).filter((p) => p.customer_id === id)),
        api.get("/tickets").then((r) => (r.data || []).filter((t) => t.customer_id === id)),
        api.get(`/customers/${id}/access`).then((r) => r.data).catch(() => ({ has_access: false })),
      ]);
      setData({ customer: c.data, apps, invoices, projects, tickets });
      setAccess(ax);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) return <div className="text-sm text-muted">Loading…</div>;
  const { customer: c, apps, invoices, projects, tickets } = data;

  const outstanding = invoices.filter((i) => ["pending", "overdue"].includes(i.status)).reduce((s, i) => s + i.amount, 0);
  const paid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);

  return (
    <div className="space-y-6">
      <button onClick={() => nav("/customers")} className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.6}/>Back to customers</button>

      <div className="flex items-start gap-4">
        <img src={c.logo_url || "/brand/soubor-one-mark.png"} alt="" className="w-16 h-16 rounded-xl object-cover ring-1 ring-[hsl(var(--border))]" onError={(e) => { e.currentTarget.src = "/brand/soubor-one-mark.png"; }}/>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">Customer · {c.industry}</div>
          <h1 className="text-[28px] font-bold tracking-tight">{c.company}</h1>
          <div className="flex items-center gap-4 text-[13px] text-[hsl(var(--text-muted))] mt-1">
            <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" strokeWidth={1.6}/>{c.email}</span>
            {c.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" strokeWidth={1.6}/>{c.phone}</span>}
            <StatusPill variant={c.status === "active" ? "success" : "default"}>{c.status}</StatusPill>
          </div>
        </div>
        <Button variant="secondary" onClick={() => setEditing(true)} data-testid="edit-customer-btn"><Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/>Edit</Button>
        <Button variant="secondary" onClick={() => setQuickCharge(true)} data-testid="quick-charge-btn"><Zap className="w-3.5 h-3.5" strokeWidth={1.6}/>Add charge</Button>
        <Button onClick={() => setGrantingAccess(true)} data-testid="grant-access-btn">
          <KeyRound className="w-3.5 h-3.5" strokeWidth={1.6}/>
          {access?.has_access ? "Manage portal access" : "Grant portal access"}
        </Button>
      </div>

      {access?.has_access && (
        <Card data-testid="access-status-card">
          <div className="px-5 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
              <Check className="w-4 h-4" strokeWidth={2.2}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold">Portal access is live</div>
              <div className="text-[11px] text-[hsl(var(--text-muted))] truncate">
                {access.user?.name} · <span className="font-mono">{access.user?.email}</span>
                {access.user?.last_login_at ? ` · last sign-in ${new Date(access.user.last_login_at).toLocaleDateString()}` : " · never signed in"}
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setGrantingAccess(true)} data-testid="reset-access-btn">
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6}/>Reset password
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="MRR" value={gbp(c.mrr)} variant="primary" />
        <KPI label="Lifetime paid" value={gbp(paid)} variant="success" />
        <KPI label="Outstanding" value={gbp(outstanding)} variant={outstanding > 0 ? "warning" : "default"} />
        <KPI label="Active apps" value={apps.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Apps">
          <div className="divide-y divide-[hsl(var(--border))]">
            {apps.map((a) => (
              <div key={a.id} className="px-5 py-3 flex items-center gap-3">
                <img src={a.icon_url} alt="" className="w-9 h-9 rounded-lg object-cover ring-1 ring-[hsl(var(--border))]"/>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate">{a.name}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">v{a.version}</div>
                </div>
                <div className="text-[12px] font-mono text-[hsl(var(--text-muted))]">{a.stats?.active_users?.toLocaleString() || 0} users</div>
              </div>
            ))}
            {apps.length === 0 && <Empty title="No apps"/>}
          </div>
        </Card>

        <Card title="Projects">
          <div className="divide-y divide-[hsl(var(--border))]">
            {projects.map((p) => (
              <div key={p.id} className="px-5 py-3">
                <div className="flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-[hsl(var(--text-muted))]" strokeWidth={1.6}/><div className="text-[13px] font-semibold">{p.name}</div></div>
                <div className="mt-2 h-1.5 bg-[hsl(var(--surface-hover))] rounded-full overflow-hidden"><div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${p.progress}%` }}/></div>
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">{p.status.replace("_", " ")} · {p.progress}%</div>
              </div>
            ))}
            {projects.length === 0 && <Empty title="No projects"/>}
          </div>
        </Card>

        <Card title="Invoices">
          <div className="divide-y divide-[hsl(var(--border))]">
            {invoices.slice(0, 6).map((i) => (
              <div key={i.id} className="px-5 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold font-mono">{i.number}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">{new Date(i.issued_date).toLocaleDateString()}</div>
                </div>
                <StatusPill variant={invoiceStatusVariant(i.status)}>{i.status}</StatusPill>
                <div className="font-mono font-semibold w-[80px] text-right">{gbp(i.amount)}</div>
              </div>
            ))}
            {invoices.length === 0 && <Empty title="No invoices"/>}
          </div>
        </Card>

        <Card title="Tickets">
          <div className="divide-y divide-[hsl(var(--border))]">
            {tickets.map((t) => (
              <div key={t.id} className="px-5 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold truncate">{t.subject}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">{t.number}</div>
                </div>
                <StatusPill variant={ticketStatusVariant(t.status)}>{t.status.replace("_", " ")}</StatusPill>
              </div>
            ))}
            {tickets.length === 0 && <Empty title="No tickets"/>}
          </div>
        </Card>
      </div>

      <CustomerSoftwareCard customerId={id} customerName={c.company}/>

      {c.notes && (
        <Card title="Notes">
          <div className="p-5 text-[13px] text-[hsl(var(--text-muted))] leading-relaxed">{c.notes}</div>
        </Card>
      )}

      {editing && <CustomerEditModal customer={c} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }}/>}
      {grantingAccess && <GrantAccessModal customer={c} access={access} onClose={() => setGrantingAccess(false)} onDone={() => { setGrantingAccess(false); load(); }}/>}
      {quickCharge && <QuickChargeModal customerId={id} customerName={c.company} onClose={() => setQuickCharge(false)} onCreated={() => { setQuickCharge(false); load(); }}/>}
    </div>
  );
}

function GrantAccessModal({ customer, access, onClose, onDone }) {
  const { tenant } = useTenant();
  const isExisting = !!access?.has_access;
  const [form, setForm] = useState({
    email: access?.user?.email || customer.email || "",
    name: access?.user?.name || customer.main_contact || customer.contact || customer.company || "",
    password: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false); // revoke confirm
  const [issued, setIssued] = useState(null); // success state with credentials to copy

  const portalUrl = (typeof window !== "undefined") ? window.location.origin : "https://soubor.one";

  const generatePassword = () => {
    const adj = ["Bright", "Swift", "Calm", "Bold", "Quiet", "Sharp", "Clever", "Sunny"];
    const noun = ["Falcon", "Harbor", "Compass", "Anchor", "Summit", "Lantern", "Quartz", "Rocket"];
    const pw = `${adj[Math.floor(Math.random()*adj.length)]}-${noun[Math.floor(Math.random()*noun.length)]}-${Math.floor(1000+Math.random()*9000)}`;
    setForm((f) => ({ ...f, password: pw }));
    setShowPw(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || form.password.length < 6) {
      toast.error("Email and a 6+ character password are required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/customers/${customer.id}/grant-access`, {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim() || null,
        password: form.password,
      });
      toast.success(isExisting ? "Password reset" : "Portal access granted");
      setIssued({ email: form.email.trim().toLowerCase(), password: form.password });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to grant access");
    } finally { setSaving(false); }
  };

  const revoke = async () => {
    setSaving(true);
    try {
      await api.post(`/customers/${customer.id}/revoke-access`);
      toast.success("Portal access revoked");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke");
    } finally { setSaving(false); setConfirming(false); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); }
  };

  const shareMessage = issued ? `Hi ${form.name || "there"}. Your ${tenant?.name || "Soubor One"} portal is ready.

Sign in: ${portalUrl}/login
Email: ${issued.email}
Temporary password: ${issued.password}

You'll see your projects, invoices, applications and what's coming up. Tip: change the password under Profile → Security after your first sign-in.
, ${tenant?.name || "Soubor One"} team` : "";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="grant-access-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold inline-flex items-center gap-1.5"><KeyRound className="w-3 h-3" strokeWidth={2}/>Portal access</div>
            <h3 className="text-lg font-semibold mt-0.5">{isExisting ? "Reset login for" : "Invite"} {customer.company}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>

        {!issued ? (
          <form onSubmit={submit}>
            <div className="p-6 space-y-4">
              <p className="text-[12.5px] text-[hsl(var(--text-muted))] leading-relaxed">
                {isExisting
                  ? `Reset the password for the existing ${customer.company} login. Their email and the link to ${tenant?.name || "Soubor One"} stay the same.`
                  : `Create a sign-in for ${customer.company}. They'll be scoped to their own projects, invoices, applications and roadmap. Nothing else.`}
              </p>
              <div>
                <Label>Email *</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={isExisting} data-testid="grant-email"/>
                {isExisting && <p className="text-[11px] text-[hsl(var(--text-muted))] mt-1">Email can't change on reset. Revoke access if you need to switch.</p>}
              </div>
              <div>
                <Label>Display name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ian Green"/>
              </div>
              <div>
                <Label>Temporary password *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input type={showPw ? "text" : "password"} required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" data-testid="grant-password"/>
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]" tabIndex={-1}>
                      {showPw ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.6}/> : <Eye className="w-3.5 h-3.5" strokeWidth={1.6}/>}
                    </button>
                  </div>
                  <Button type="button" variant="secondary" onClick={generatePassword} data-testid="generate-password-btn"><RefreshCw className="w-3.5 h-3.5" strokeWidth={1.6}/>Generate</Button>
                </div>
                <p className="text-[11px] text-[hsl(var(--text-muted))] mt-1">Share this with the customer once. They can change it under Profile after their first sign-in.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-between gap-2">
              {isExisting ? (
                <button type="button" onClick={() => setConfirming(true)} className="text-[12px] font-semibold text-rose-600 dark:text-rose-400 inline-flex items-center gap-1 hover:underline" data-testid="revoke-access-btn">
                  <ShieldOff className="w-3.5 h-3.5" strokeWidth={1.6}/>Revoke portal access
                </button>
              ) : <span/>}
              <div className="flex items-center gap-2">
                <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="grant-submit-btn">
                  {saving ? "Saving…" : isExisting ? "Reset password" : "Create login"}
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <div className="p-6 space-y-4" data-testid="grant-success">
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4">
              <div className="inline-flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-semibold text-[13px]">
                <Check className="w-4 h-4" strokeWidth={2.2}/>{isExisting ? "Password reset · share the new credentials" : "Portal account ready · share these details"}
              </div>
              <p className="text-[12px] text-[hsl(var(--text-muted))] mt-1.5 leading-relaxed">
                Send the email + temporary password to {customer.company}. They'll land on the {tenant?.name || "Soubor One"} sign-in page.
              </p>
            </div>

            <div className="rounded-xl border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))] bg-[hsl(var(--background))] overflow-hidden">
              <CredRow label="Sign-in URL" value={`${portalUrl}/login`} onCopy={copy}/>
              <CredRow label="Email" value={issued.email} onCopy={copy} mono/>
              <CredRow label="Temporary password" value={issued.password} onCopy={copy} mono/>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Ready-to-send message</Label>
                <button onClick={() => copy(shareMessage)} className="text-[11px] font-semibold text-[hsl(var(--primary))] inline-flex items-center gap-1 hover:underline" data-testid="copy-share-msg"><Copy className="w-3 h-3" strokeWidth={2}/>Copy message</button>
              </div>
              <Textarea rows={9} readOnly value={shareMessage} className="font-mono text-[12px]"/>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button type="button" onClick={onDone} data-testid="grant-done-btn">Done</Button>
            </div>
          </div>
        )}

        {confirming && (
          <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => !saving && setConfirming(false)}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl p-6">
              <h4 className="text-lg font-semibold mb-2">Revoke portal access?</h4>
              <p className="text-[13px] text-[hsl(var(--text-muted))] leading-relaxed mb-4">
                {customer.company} will no longer be able to sign in. You can grant access again at any time. Their data stays untouched.
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="secondary" onClick={() => setConfirming(false)} disabled={saving}>Cancel</Button>
                <Button type="button" onClick={revoke} disabled={saving} data-testid="revoke-confirm-btn">{saving ? "Revoking…" : "Yes, revoke"}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CredRow({ label, value, onCopy, mono }) {
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <div className="w-32 shrink-0 text-[10px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold">{label}</div>
      <div className={`flex-1 truncate text-[13px] ${mono ? "font-mono" : ""}`}>{value}</div>
      <button type="button" onClick={() => onCopy(value)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] transition-colors" title={`Copy ${label.toLowerCase()}`} data-testid={`copy-${label.toLowerCase().replace(/\s+/g,"-")}`}>
        <Copy className="w-3.5 h-3.5" strokeWidth={1.6}/>
      </button>
    </div>
  );
}

const INDUSTRIES = ["Sports", "Retail", "Finance", "Media", "Education", "SaaS", "Other"];

function CustomerEditModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({
    company: customer.company || "",
    main_contact: customer.main_contact || "",
    email: customer.email || "",
    phone: customer.phone || "",
    industry: customer.industry || "Other",
    status: customer.status || "active",
    mrr: customer.mrr ?? 0,
    logo_url: customer.logo_url || "",
    notes: customer.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/customers/${customer.id}`, { ...form, mrr: parseFloat(form.mrr) || 0 });
      toast.success("Customer updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-edit-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <h3 className="text-lg font-semibold">Edit {customer.company}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Company *</Label><Input required value={form.company} onChange={s("company")} data-testid="cust-company"/></div>
          <div><Label>Main contact</Label><Input value={form.main_contact} onChange={s("main_contact")}/></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={s("email")}/></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={s("phone")}/></div>
          <div><Label>Industry</Label>
            <Select value={form.industry} onChange={s("industry")}>
              {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
            </Select>
          </div>
          <div><Label>Status</Label>
            <Select value={form.status} onChange={s("status")}>
              <option value="active">Active</option>
              <option value="prospect">Prospect</option>
              <option value="churned">Churned</option>
            </Select>
          </div>
          <div><Label>MRR (£)</Label><Input type="number" min="0" step="0.01" value={form.mrr} onChange={s("mrr")}/></div>
          <div className="md:col-span-2"><Label>Logo URL</Label><Input value={form.logo_url} onChange={s("logo_url")} placeholder="https://…"/></div>
          <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={s("notes")}/></div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="cust-save-btn">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </div>
  );
}


function CustomerSoftwareCard({ customerId, customerName }) {
  const [data, setData] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [showAllocate, setShowAllocate] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [my, all] = await Promise.all([
        api.get(`/customers/${customerId}/products`),
        api.get("/products"),
      ]);
      setData(my.data);
      setAllProducts(all.data || []);
    } catch (e) { console.error(e); setData({ items: [], totals: { mrr: 0, setup_booked: 0, count: 0 } }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const onRemove = async (item) => {
    if (!window.confirm(`Remove ${item.product_name} from ${customerName}? They'll lose access in their portal.`)) return;
    try {
      await api.delete(`/products/${item.product_id}/allocations/${customerId}`);
      toast.success(`${item.product_name} unallocated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to unallocate");
    }
  };

  if (!data) {
    return <Card title="Software"><div className="p-5 text-[13px] text-[hsl(var(--text-muted))]">Loading…</div></Card>;
  }

  const { items, totals } = data;

  return (
    <Card
      title={<span className="inline-flex items-center gap-2"><Boxes className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.8}/>Software & subscriptions</span>}
      right={
        <div className="flex items-center gap-3">
          {totals.mrr > 0 && (
            <span className="text-[11px] font-mono text-[hsl(var(--text-muted))]">
              <span className="font-bold text-[hsl(var(--primary))]">{gbp(totals.mrr)}</span>/mo <span className="text-[hsl(var(--text-subtle))]">ex VAT</span>
            </span>
          )}
          <Button size="sm" onClick={() => setShowAllocate(true)} data-testid="allocate-product-to-customer-btn">
            <Plus className="w-3.5 h-3.5" strokeWidth={2}/>Allocate software
          </Button>
        </div>
      }
      data-testid="customer-software-card"
    >
      {items.length === 0 ? (
        <Empty title="No software allocated yet" subtitle={`Allocate a product to ${customerName} and set their custom URL + pricing.`} />
      ) : (
        <div className="divide-y divide-[hsl(var(--border))]">
          {items.map((item) => {
            const a = item.allocation || {};
            const isPending = a.status === "pending_payment";
            const onActivate = async () => {
              if (!window.confirm(`Mark payment received for ${item.product_name}? ${customerName} will get access immediately.`)) return;
              try {
                await api.post(`/products/${item.product_id}/allocations/${customerId}/activate`);
                toast.success(`${item.product_name} activated`);
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed");
              }
            };
            return (
              <div key={item.product_id} className={`px-5 py-4 flex items-center gap-4 ${isPending ? "bg-amber-500/[0.04] border-l-2 border-amber-500" : ""}`} data-testid="customer-software-row">
                {item.product_icon
                  ? <img src={item.product_icon} alt="" className="w-10 h-10 rounded-lg object-cover ring-1 ring-[hsl(var(--border))] shrink-0"/>
                  : <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary-muted))] text-[hsl(var(--primary))] grid place-items-center font-bold text-[13px] shrink-0">{(item.product_name || "?").slice(0, 2).toUpperCase()}</div>}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[14px] font-semibold truncate">{item.product_name}</div>
                    {isPending ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400" data-testid="alloc-pending-pill">
                        <KeyRound className="w-2.5 h-2.5" strokeWidth={2.2}/>Awaiting payment
                      </span>
                    ) : (
                      <StatusPill variant={a.status === "active" ? "success" : a.status === "trial" ? "primary" : a.status === "paused" ? "warning" : "default"}>{a.status || "active"}</StatusPill>
                    )}
                    {a.invoice_id && <span className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--text-muted))] font-mono">linked to invoice</span>}
                  </div>
                  {(a.custom_url || item.deployment_url) && (
                    <a
                      href={a.custom_url || item.deployment_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12px] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1 truncate max-w-full mt-0.5"
                    >
                      {a.custom_url || item.deployment_url}
                      <ExternalLink className="w-3 h-3 shrink-0" strokeWidth={1.6}/>
                    </a>
                  )}
                  {a.activated_at ? (
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-subtle))] font-semibold mt-1">Active since {new Date(a.activated_at).toLocaleDateString()}</div>
                  ) : a.started_at && (
                    <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--text-subtle))] font-semibold mt-1">{isPending ? "Awaiting payment since" : "Allocated"} {new Date(a.started_at).toLocaleDateString()}</div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-semibold text-[14px]">
                    <span className={isPending ? "text-amber-600 dark:text-amber-400" : "text-[hsl(var(--primary))]"}>{gbp(a.monthly_fee)}</span>
                    <span className="text-[10px] font-normal text-[hsl(var(--text-muted))]">/mo</span>
                  </div>
                  {(a.setup_fee || 0) > 0 && (
                    <div className="text-[11px] text-[hsl(var(--text-muted))] font-mono mt-0.5">
                      +{gbp(a.setup_fee)} setup
                    </div>
                  )}
                </div>
                {isPending && (
                  <Button size="sm" variant="secondary" onClick={onActivate} className="shrink-0" data-testid="mark-paid-activate-btn">
                    <Check className="w-3.5 h-3.5" strokeWidth={2}/>Mark as paid
                  </Button>
                )}
                <div className="flex items-center gap-0.5 opacity-60 hover:opacity-100 transition-opacity shrink-0">
                  <button onClick={() => setEditing(item)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))]" title="Edit allocation" data-testid="edit-allocation-btn">
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.6}/>
                  </button>
                  <button onClick={() => onRemove(item)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600" title="Remove" data-testid="remove-allocation-btn">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAllocate && (
        <AllocateForCustomerModal
          customerId={customerId}
          customerName={customerName}
          products={allProducts}
          existing={items}
          onClose={() => setShowAllocate(false)}
          onSaved={() => { setShowAllocate(false); load(); }}
        />
      )}
      {editing && (
        <AllocateForCustomerModal
          customerId={customerId}
          customerName={customerName}
          products={allProducts}
          existing={items}
          editItem={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </Card>
  );
}

function AllocateForCustomerModal({ customerId, customerName, products, existing, editItem, onClose, onSaved }) {
  const isEdit = !!editItem;
  const existingIds = new Set((existing || []).map((e) => e.product_id));
  const eligible = products.filter((p) => isEdit ? p.id === editItem.product_id : !existingIds.has(p.id));
  const [productId, setProductId] = useState(editItem?.product_id || "");
  const product = products.find((p) => p.id === productId);
  const [invoices, setInvoices] = useState([]);

  const [form, setForm] = useState(() => ({
    custom_url: editItem?.allocation?.custom_url || "",
    setup_fee: editItem?.allocation?.setup_fee ?? 0,
    monthly_fee: editItem?.allocation?.monthly_fee ?? 0,
    status: editItem?.allocation?.status || "pending_payment",
    invoice_id: editItem?.allocation?.invoice_id || "",
    notes: editItem?.allocation?.notes || "",
  }));
  const [saving, setSaving] = useState(false);

  // Load that customer's pending invoices for the picker
  useEffect(() => {
    api.get("/invoices").then(({ data }) => {
      const mine = (data || []).filter((i) => i.customer_id === customerId && i.status !== "draft" && i.status !== "void");
      setInvoices(mine);
    }).catch(() => setInvoices([]));
  }, [customerId]);

  useEffect(() => {
    if (isEdit || !product) return;
    setForm((f) => ({
      ...f,
      setup_fee: product.default_setup_fee ?? 0,
      monthly_fee: product.default_monthly_fee ?? 0,
    }));
  }, [productId, isEdit, product]);

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) { toast.error("Pick a product"); return; }
    setSaving(true);
    try {
      await api.post(`/products/${productId}/allocations`, {
        customer_id: customerId,
        custom_url: form.custom_url || null,
        setup_fee: parseFloat(form.setup_fee) || 0,
        monthly_fee: parseFloat(form.monthly_fee) || 0,
        status: form.status,
        invoice_id: form.invoice_id || null,
        notes: form.notes || null,
      });
      toast.success(isEdit ? "Allocation updated" : (form.status === "active" ? "Software activated" : "Allocated · awaiting payment"));
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="customer-allocate-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold inline-flex items-center gap-1.5"><Boxes className="w-3 h-3" strokeWidth={2}/>Software</div>
            <h3 className="text-lg font-semibold mt-0.5">{isEdit ? `Edit ${editItem.product_name}` : "Allocate software"} · {customerName}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label>Product *</Label>
            <Select value={productId} onChange={(e) => setProductId(e.target.value)} disabled={isEdit} data-testid="customer-alloc-product">
              <option value="">Select…</option>
              {eligible.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
            {eligible.length === 0 && !isEdit && (
              <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                {customerName} already has every product. Add more software on /products first.
              </div>
            )}
          </div>
          <div>
            <Label>Custom URL (optional)</Label>
            <Input value={form.custom_url} onChange={(e) => setForm({ ...form, custom_url: e.target.value })} placeholder={product?.deployment_url || "https://customer.vantage.io"}/>
            <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">Leave blank to use the product default.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Setup fee (£, ex VAT)</Label>
              <Input type="number" min="0" step="0.01" value={form.setup_fee} onChange={(e) => setForm({ ...form, setup_fee: e.target.value })} data-testid="customer-alloc-setup"/>
              {parseFloat(form.setup_fee || 0) > 0 && <div className="text-[10px] text-[hsl(var(--text-muted))] mt-1">{gbp(parseFloat(form.setup_fee) * 1.2)} inc VAT</div>}
            </div>
            <div>
              <Label>Monthly (£/mo, ex VAT)</Label>
              <Input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} data-testid="customer-alloc-monthly"/>
              {parseFloat(form.monthly_fee || 0) > 0 && <div className="text-[10px] text-[hsl(var(--text-muted))] mt-1">{gbp(parseFloat(form.monthly_fee) * 1.2)}/mo inc VAT</div>}
            </div>
          </div>

          {/* Access / status radio */}
          <div>
            <Label>Access</Label>
            <div className="grid grid-cols-2 gap-2 mt-1" data-testid="alloc-access-radio">
              <button
                type="button"
                onClick={() => setForm({ ...form, status: "pending_payment" })}
                className={`text-left p-3 rounded-lg border transition-all ${form.status === "pending_payment" ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary-muted))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`}
                data-testid="alloc-status-pending"
              >
                <div className="text-[12px] font-semibold inline-flex items-center gap-1.5"><KeyRound className="w-3 h-3" strokeWidth={2}/>Needs payment first</div>
                <div className="text-[10.5px] text-[hsl(var(--text-muted))] mt-1 leading-snug">Customer sees it but can't open it. Auto-activates on invoice paid, or activate manually.</div>
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, status: "active" })}
                className={`text-left p-3 rounded-lg border transition-all ${form.status === "active" ? "border-[hsl(var(--success))] bg-emerald-500/10" : "border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))]"}`}
                data-testid="alloc-status-active"
              >
                <div className="text-[12px] font-semibold inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400"><Check className="w-3 h-3" strokeWidth={2.5}/>Active now</div>
                <div className="text-[10.5px] text-[hsl(var(--text-muted))] mt-1 leading-snug">Already paid / pre-paid / free. Customer gets access immediately.</div>
              </button>
            </div>
            {!["pending_payment", "active"].includes(form.status) && (
              <div className="mt-2">
                <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="trial">Trial</option>
                  <option value="paused">Paused</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </div>
            )}
          </div>

          {form.status === "pending_payment" && (
            <div>
              <Label>Link to invoice (optional)</Label>
              <Select value={form.invoice_id} onChange={(e) => setForm({ ...form, invoice_id: e.target.value })} data-testid="alloc-invoice-picker">
                <option value="">(No invoice yet · activate manually)</option>
                {invoices.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.number} · {i.status} · £{(i.amount || 0).toLocaleString()} · due {i.due_date ? new Date(i.due_date).toLocaleDateString() : "—"}
                  </option>
                ))}
              </Select>
              <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">When this invoice flips to paid (Stripe webhook or you mark it paid), {customerName} gets access automatically.</div>
            </div>
          )}

          <div>
            <Label>Notes (internal)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Contract refs, onboarding notes…"/>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving || (!isEdit && eligible.length === 0)} data-testid="customer-alloc-save-btn">
            {saving ? "Saving…" : isEdit ? "Save changes" : (form.status === "active" ? "Allocate & activate" : "Allocate · awaiting payment")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function QuickChargeModal({ customerId, customerName, onClose, onCreated }) {
  const [form, setForm] = useState({
    description: "",
    amount_ex_vat: "",
    vat_rate: 0.20,
    due_in_days: 14,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const exVat = parseFloat(form.amount_ex_vat || 0);
  const incVat = exVat * (1 + parseFloat(form.vat_rate || 0));
  const vatAmt = incVat - exVat;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Add a short description"); return; }
    if (exVat <= 0) { toast.error("Amount must be greater than zero"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/customers/${customerId}/quick-charge`, {
        customer_id: customerId,
        description: form.description.trim(),
        amount_ex_vat: exVat,
        vat_rate: parseFloat(form.vat_rate || 0),
        due_in_days: parseInt(form.due_in_days || 14, 10),
        notes: form.notes.trim() || null,
      });
      toast.success(`Invoice ${data.number} created · ${gbp(incVat)} inc VAT`);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create invoice");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg rounded-2xl bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="quick-charge-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))] font-semibold inline-flex items-center gap-1.5"><Receipt className="w-3 h-3" strokeWidth={2}/>Quick invoice</div>
            <h3 className="text-lg font-semibold mt-0.5">Add a charge for {customerName}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-md hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-[12.5px] text-[hsl(var(--text-muted))] leading-relaxed">
            For one-off work outside of an accepted quote. Creates an invoice (status <span className="font-mono">pending</span>) right away. You can mark it paid on the Invoices page once the funds clear.
          </p>
          <div>
            <Label>What's it for *</Label>
            <Input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. One-off website refresh, content migration, on-site visit" data-testid="quick-charge-description"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount (£, ex VAT)</Label>
              <Input type="number" min="0" step="0.01" required value={form.amount_ex_vat} onChange={(e) => setForm({ ...form, amount_ex_vat: e.target.value })} placeholder="500.00" data-testid="quick-charge-amount"/>
            </div>
            <div>
              <Label>VAT rate</Label>
              <Select value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}>
                <option value="0.20">20% (standard)</option>
                <option value="0.05">5% (reduced)</option>
                <option value="0">0% (exempt)</option>
              </Select>
            </div>
          </div>
          {exVat > 0 && (
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4 space-y-1.5" data-testid="quick-charge-preview">
              <div className="flex items-center justify-between text-[12px] text-[hsl(var(--text-muted))]">
                <span>Subtotal (ex VAT)</span>
                <span className="font-mono">{gbp(exVat)}</span>
              </div>
              <div className="flex items-center justify-between text-[12px] text-[hsl(var(--text-muted))]">
                <span>VAT @ {Math.round(parseFloat(form.vat_rate) * 100)}%</span>
                <span className="font-mono">{gbp(vatAmt)}</span>
              </div>
              <div className="flex items-center justify-between text-[15px] font-semibold pt-1.5 border-t border-[hsl(var(--border))]">
                <span>Customer pays</span>
                <span className="font-mono text-[hsl(var(--primary))]">{gbp(incVat)} <span className="text-[10px] font-normal text-[hsl(var(--text-muted))]">inc VAT</span></span>
              </div>
            </div>
          )}
          <div>
            <Label>Due in (days)</Label>
            <Input type="number" min="0" max="120" value={form.due_in_days} onChange={(e) => setForm({ ...form, due_in_days: e.target.value })}/>
          </div>
          <div>
            <Label>Notes (visible on invoice)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything {customerName} should see when they look at this invoice."/>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2 bg-[hsl(var(--background))]/50">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="quick-charge-submit-btn">
            {saving ? "Creating…" : (<><Zap className="w-3.5 h-3.5" strokeWidth={2}/>Create invoice</>)}
          </Button>
        </div>
      </form>
    </div>
  );
}

